import { FEATURE_FLAGS, type DisabledBehavior, type FlagDefinition, type FlagKey } from '../config/flags'
import { getRuntimeConfig } from './runtimeConfig'

const flag = (key: FlagKey): FlagDefinition => FEATURE_FLAGS[key]

const TRUE_VALUES = new Set(['true', '1', 'yes', 'on'])
const FALSE_VALUES = new Set(['false', '0', 'no', 'off'])

function parseBool(raw: string | undefined): boolean | undefined {
  /* v8 ignore start */
  if (raw === undefined) return undefined
  /* v8 ignore stop */
  const lowered = raw.trim().toLowerCase()
  if (TRUE_VALUES.has(lowered)) return true
  if (FALSE_VALUES.has(lowered)) return false
  return undefined
}

function envValue(key: FlagKey): boolean | undefined {
  return parseBool(import.meta.env[`VITE_FEATURE_${key}` as keyof ImportMetaEnv] as string | undefined)
}

// Host overrides: set only by the embeddable header (src/embed), e.g. to show
// the account entry when the host app supplies its own account link. The
// website itself never sets these, so for the site the order below is unchanged.
let hostOverrides: Partial<Record<FlagKey, boolean>> = {}

/** Replaces the host overrides (pass {} to clear them). */
export function setFlagOverrides(flags: Partial<Record<FlagKey, boolean>>): void {
  hostOverrides = { ...flags }
}

/**
 * Resolution order: host override (embed only) > runtime JSON override > env
 * var > default. Runtime wins over env so ops can flip flags without redeploying.
 */
export function isFeatureEnabled(key: FlagKey): boolean {
  const host = hostOverrides[key]
  if (host !== undefined) return host
  const runtime = getRuntimeConfig().flags?.[key]
  if (runtime !== undefined) return runtime
  const env = envValue(key)
  if (env !== undefined) return env
  return flag(key).defaultEnabled
}

const VALID_DISABLED: ReadonlySet<DisabledBehavior> = new Set(['hide', 'coming-soon', 'maintenance'])

function parseDisabledAs(raw: string | undefined): DisabledBehavior | undefined {
  if (!raw) return undefined
  return VALID_DISABLED.has(raw as DisabledBehavior) ? (raw as DisabledBehavior) : undefined
}

/** What to render for a disabled page: hide (404), coming-soon, or maintenance. */
export function getDisabledBehavior(key: FlagKey): DisabledBehavior {
  const runtime = getRuntimeConfig().disabledBehavior?.[key]
  if (runtime) return runtime
  const perFlagEnv = parseDisabledAs(
    import.meta.env[`VITE_FEATURE_${key}_DISABLED_AS` as keyof ImportMetaEnv] as string | undefined,
  )
  if (perFlagEnv) return perFlagEnv
  const definitionDefault = flag(key).disabledAs
  if (definitionDefault) return definitionDefault
  const globalDefault = parseDisabledAs(import.meta.env.VITE_FEATURE_DEFAULT_DISABLED_AS as string | undefined)
  return globalDefault ?? 'hide'
}

/** True when the entire site is in maintenance mode. */
export function isSiteMaintenance(): boolean {
  const runtime = getRuntimeConfig().maintenance?.siteWide
  if (runtime !== undefined) return runtime
  return parseBool(import.meta.env.VITE_MAINTENANCE_MODE as string | undefined) ?? false
}

export function getMaintenanceMessage(): string {
  const envMsg = (import.meta.env.VITE_MAINTENANCE_MESSAGE as string | undefined)?.trim()
  return (
    getRuntimeConfig().maintenance?.message ||
    (envMsg && envMsg.length > 0 ? envMsg : undefined) ||
    "We're making improvements. Please check back shortly."
  )
}
